<template>
  <div>
    <div class="row g-3 mb-3">
      <div class="col-md-7">
        <label class="form-label small fw-semibold">Nom du rayon *</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.name"
          required
        />
      </div>
      <div class="col-md-5">
        <label class="form-label small fw-semibold">Code</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.code"
          placeholder="Ex : R-AB, PARA, DIGI..."
        />
        <small class="text-muted">
          Laisser vide pour générer automatiquement.
        </small>
      </div>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-4">
        <label class="form-label small fw-semibold">Position</label>
        <PrimevueInputNumber
          class="w-100"
          :min="0"
          v-model="form.position"
        />
        <small class="text-muted">
          Ordre dans les listes (0 = par défaut).
        </small>
      </div>
      <div class="col-md-8">
        <label class="form-label small fw-semibold">Actif</label>
        <div class="form-check d-flex align-items-center gap-2 ps-0">
          <PrimevueCheckbox v-model="form.is_active" binary inputId="rayonActive" />
          <label class="form-check-label small" for="rayonActive">
            Disponibles pour l’affectation des produits
          </label>
        </div>
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label small fw-semibold">Couleur du rayon</label>
      <div class="rayon-color-control">
        <div class="rayon-color-palette" aria-label="Palette de couleurs">
          <button
            v-for="color in colorPalette"
            :key="color"
            type="button"
            class="rayon-color-swatch"
            :class="{ active: normalizedColor === color }"
            :style="{ backgroundColor: color }"
            :title="color"
            @click="setColor(color)"
          />
        </div>

        <div class="d-flex align-items-center gap-2 flex-wrap">
          <PrimevueColorPicker v-model="colorPickerValue" format="hex" />
          <PrimevueInputText
            v-model.trim="form.color"
            class="rayon-color-input"
            maxlength="7"
            placeholder="#0d9488"
            @blur="normalizeColorField"
          />
          <div class="rayon-color-preview" :style="{ borderColor: normalizedColor }">
            <span class="rayon-color-dot" :style="{ backgroundColor: normalizedColor }"></span>
            <span>{{ form.name || 'Aperçu du rayon' }}</span>
            <small v-if="form.code" class="text-muted">({{ form.code }})</small>
          </div>
        </div>
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label small fw-semibold">Description</label>
      <PrimevueTextarea
        class="w-100"
        rows="2"
        v-model="form.description"
        autoResize
        placeholder="Infos sur le contenu, l’emplacement, type de produits..."
      />
    </div>

    <div v-if="mode === 'create'" class="form-check d-flex align-items-center gap-2 ps-0 mb-3">
      <PrimevueCheckbox v-model="form.create_in_all_shops" binary inputId="rayonCreateInAllShops" />
      <label class="form-check-label small" for="rayonCreateInAllShops">
        Créer aussi dans tous les sites actifs
      </label>
    </div>

    <div class="d-flex justify-content-end gap-2 mt-3">
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        :disabled="submitting"
        @click="$emit('cancel')"
      >
        Annuler
      </button>
      <AppLoadingButton
        type="button"
        class="btn btn-primary btn-sm"
        :loading="submitting"
        loading-label="Enregistrement..."
        @click="submit"
      >
        <i class="fa fa-save me-1"></i>
        Enregistrer
      </AppLoadingButton>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import {
  colorToPickerValue,
  DEFAULT_RAYON_COLOR,
  normalizeRayonColor,
  pickerValueToColor,
  RAYON_COLOR_PALETTE,
} from '@/utils/rayonColor';

interface RayFormData {
  id?: number;
  name: string;
  code: string | null;
  color: string;
  position: number;
  is_active: boolean;
  description: string | null;
  create_in_all_shops: boolean;
}

export default defineComponent({
  name: 'RayForm',
  props: {
    mode: {
      type: String as PropType<'create' | 'edit'>,
      required: true,
    },
    rayon: {
      type: Object as PropType<Partial<RayFormData>>,
      default: () => ({}),
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        id: undefined,
        name: '',
        code: '',
        color: DEFAULT_RAYON_COLOR,
        position: 0,
        is_active: true,
        description: '',
        create_in_all_shops: false,
      } as RayFormData,
    };
  },
  mounted() {
    this.initForm();
  },
  watch: {
    mode() {
      this.initForm();
    },
    rayon: {
      deep: true,
      handler() {
        this.initForm();
      },
    },
  },
  computed: {
    colorPalette(): string[] {
      return RAYON_COLOR_PALETTE;
    },
    normalizedColor(): string {
      return normalizeRayonColor(this.form.color);
    },
    colorPickerValue: {
      get(): string {
        return colorToPickerValue(this.form.color);
      },
      set(value: string) {
        this.form.color = pickerValueToColor(value);
      },
    },
  },
  methods: {
    initForm() {
      if (this.mode === 'edit' && this.rayon && this.rayon.id) {
        this.form.id = this.rayon.id;
        this.form.name = this.rayon.name || '';
        this.form.code = this.rayon.code || '';
        this.form.color = normalizeRayonColor(this.rayon.color);
        this.form.position = this.rayon.position ?? 0;
        this.form.is_active = this.rayon.is_active ?? true;
        this.form.description = this.rayon.description || '';
        this.form.create_in_all_shops = false;
      } else {
        this.form = {
          id: undefined,
          name: '',
          code: '',
          color: DEFAULT_RAYON_COLOR,
          position: 0,
          is_active: true,
          description: '',
          create_in_all_shops: false,
        };
      }
    },

    setColor(color: string) {
      this.form.color = normalizeRayonColor(color);
    },

    normalizeColorField() {
      this.form.color = normalizeRayonColor(this.form.color);
    },

    submit() {
      if (this.submitting) return;

      if (!this.form.name.trim()) {
        alert('Le nom du rayon est obligatoire.');
        return;
      }

      const payload: Record<string, unknown> = {
        id: this.form.id,
        name: this.form.name.trim(),
        code: this.form.code || null,
        color: normalizeRayonColor(this.form.color),
        position: this.form.position ?? 0,
        is_active: this.form.is_active,
        description: this.form.description || null,
      };

      if (this.mode === 'create') {
        payload.create_in_all_shops = this.form.create_in_all_shops;
      }

      this.$emit('save', payload);
    },
  },
});
</script>

<style scoped>
.rayon-color-control {
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  padding: 0.75rem;
}

.rayon-color-palette {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-bottom: 0.75rem;
}

.rayon-color-swatch {
  border: 2px solid #ffffff;
  border-radius: 999px;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.18);
  height: 28px;
  width: 28px;
}

.rayon-color-swatch.active {
  box-shadow: 0 0 0 2px #0f172a;
}

.rayon-color-input {
  max-width: 120px;
}

.rayon-color-preview {
  align-items: center;
  background: #ffffff;
  border: 1px solid;
  border-radius: 999px;
  display: inline-flex;
  gap: 0.35rem;
  min-height: 34px;
  padding: 0.25rem 0.75rem;
}

.rayon-color-dot {
  border-radius: 999px;
  display: inline-block;
  height: 0.75rem;
  width: 0.75rem;
}
</style>
